// src/components/Profile.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { axiosInstance } from "../utils/axiosConfig";
import { validatePhone } from "../utils/validation";
import '../styles/Profile.css';

interface JobSearchLocation {
  country: string;
  province: string;
}

interface ProgrammeOfStudy {
  [key: string]: string;
}

interface ProfileFormData {
  fullName: string;
  phone: string;
  jobBoard: string;
  qualifications: string[];
  programmeOfStudy: ProgrammeOfStudy;
  experienceLevel: string;
  yearsOfExperience: number | null;
  certifications: string[];
  jobSearchLocation: JobSearchLocation;
  industries: string[];
  preferredJobTitles: string[];
  preferredSector: string;
}

const QUALIFICATION_OPTIONS = [
  'BSc',
  'MSc',
  'PhD',
  'Professional Cert',
];

const EXPERIENCE_LEVELS = ['Junior', 'Intermediate', 'Advanced'];

const COMMON_INDUSTRIES = [
  "Technology",
  "Healthcare",
  "Finance",
  "Education",
  "Manufacturing",
  "Retail",
  "Consulting",
  "Media",
  "Energy",
  "Non-Profit",
];

const PREFERRED_SECTORS = ["Private", "Public (Government + Crown Corporations)"];

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const profilePicInputRef = React.useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<ProfileFormData>({
    fullName: "",
    phone: "",
    jobBoard: "Indeed",
    qualifications: [],
    programmeOfStudy: {},
    experienceLevel: "",
    yearsOfExperience: null,
    certifications: [],
    jobSearchLocation: { country: "", province: "" },
    industries: [],
    preferredJobTitles: [],
    preferredSector: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profilePic, setProfilePic] = useState<string | null>(null);
  const [profilePicFile, setProfilePicFile] = useState<File | null>(null);
  const [profilePicPreview, setProfilePicPreview] = useState<string | null>(null);
  const [uploadingProfilePic, setUploadingProfilePic] = useState(false);
  const [fullNameLocked, setFullNameLocked] = useState(false);

  // Resume states
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeUploading, setResumeUploading] = useState(false);
  const [resumeUploadSuccess, setResumeUploadSuccess] = useState(false);
  const [resumeUploadError, setResumeUploadError] = useState<string | null>(null);
  const [currentResume, setCurrentResume] = useState<{ id: string; fileName: string; downloadUrl: string } | null>(null);

  // Build a renderable URL for an authenticated profile-pic endpoint. The
  // backend accepts the JWT either via the Authorization header or a `token`
  // query parameter; we use the latter so a plain <img src> tag works.
  // A timestamp is appended to bust the browser cache after re-uploads.
  const buildProfilePicUrl = (url: string | null | undefined): string | null => {
    if (!url) return null;
    const token = localStorage.getItem("token") || "";
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}token=${encodeURIComponent(token)}&t=${Date.now()}`;
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axiosInstance.get("/auth/profile");
        setFormData({
          fullName: response.data.fullName || "",
          phone: response.data.phone || "",
          jobBoard: response.data.jobBoard || "Indeed",
          qualifications: response.data.qualifications || [],
          programmeOfStudy: response.data.programmeOfStudy || {},
          experienceLevel: response.data.experienceLevel || "",
          yearsOfExperience: response.data.yearsOfExperience ?? null,
          certifications: response.data.certifications || [],
          jobSearchLocation: response.data.jobSearchLocation || { country: "", province: "" },
          industries: response.data.industries || [],
          preferredJobTitles: response.data.preferredJobTitles || [],
          preferredSector: response.data.preferredSector || "",
        });
        setFullNameLocked(Boolean(response.data.fullName));
        setProfilePic(buildProfilePicUrl(response.data.profilePicUrl));
        setCurrentResume(response.data.primaryResume || null);
      } catch (err) {
        toast.error("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "yearsOfExperience") {
      setFormData(prev => ({ ...prev, [name]: value ? parseInt(value, 10) : null }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle qualifications checkbox toggle
  const handleQualificationToggle = (qual: string) => {
    setFormData(prev => {
      const newQuals = prev.qualifications.includes(qual)
        ? prev.qualifications.filter(q => q !== qual)
        : [...prev.qualifications, qual];
      // Remove programme of study for removed qualification
      if (!newQuals.includes(qual)) {
        const newProgrammes = { ...prev.programmeOfStudy };
        delete newProgrammes[qual];
        return { ...prev, qualifications: newQuals, programmeOfStudy: newProgrammes };
      }
      return { ...prev, qualifications: newQuals };
    });
  };

  // Handle programme of study change
  const handleProgrammeChange = (qual: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      programmeOfStudy: { ...prev.programmeOfStudy, [qual]: value },
    }));
  };

  // Handle job search location change
  const handleLocationChange = (field: "country" | "province", value: string) => {
    setFormData(prev => ({
      ...prev,
      jobSearchLocation: { ...prev.jobSearchLocation, [field]: value },
    }));
  };

  // Handle industries toggle
  const handleIndustryToggle = (industry: string) => {
    setFormData(prev => ({
      ...prev,
      industries: prev.industries.includes(industry)
        ? prev.industries.filter(i => i !== industry)
        : [...prev.industries, industry],
    }));
  };

  // State for adding new items
  const [newCertification, setNewCertification] = useState("");
  const [newJobTitle, setNewJobTitle] = useState("");

  // Handle adding certification
  const handleAddCertification = () => {
    if (newCertification.trim() && !formData.certifications.includes(newCertification.trim())) {
      setFormData(prev => ({
        ...prev,
        certifications: [...prev.certifications, newCertification.trim()],
      }));
      setNewCertification("");
    }
  };

  // Handle removing certification
  const handleRemoveCertification = (cert: string) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter(c => c !== cert),
    }));
  };

  // Handle adding preferred job title (max 5)
  const handleAddJobTitle = () => {
    if (formData.preferredJobTitles.length >= 5) {
      toast.error("Maximum 5 preferred job titles allowed");
      return;
    }
    if (newJobTitle.trim() && !formData.preferredJobTitles.includes(newJobTitle.trim())) {
      setFormData(prev => ({
        ...prev,
        preferredJobTitles: [...prev.preferredJobTitles, newJobTitle.trim()],
      }));
      setNewJobTitle("");
    }
  };

  // Handle removing preferred job title
  const handleRemoveJobTitle = (title: string) => {
    setFormData(prev => ({
      ...prev,
      preferredJobTitles: prev.preferredJobTitles.filter(t => t !== title),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate phone format (optional field) and industries cap before saving.
    const phoneCheck = validatePhone(formData.phone || "");
    if (!phoneCheck.valid) {
      toast.error(phoneCheck.message || "Enter a valid phone number");
      return;
    }
    if (formData.industries.length > 3) {
      toast.error("You may select a maximum of 3 industries.");
      return;
    }

    setSaving(true);
    try {
      await axiosInstance.put("/auth/profile", formData);
      toast.success("Profile updated!");
      if (formData.fullName) {
        setFullNameLocked(true);
      }
    } catch (err) {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  // New handler for resume upload
  const handleResumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setResumeFile(e.target.files[0]);
    }
  };

  const handleResumeUpload = async () => {
    if (!resumeFile) {
      toast.error("Please select a resume file first");
      return;
    }

    setResumeUploading(true);
    setResumeUploadSuccess(false);
    setResumeUploadError(null);

    const formDataUpload = new FormData();
    formDataUpload.append("resume", resumeFile);

    try {
      await axiosInstance.post("/auth/resume", formDataUpload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setResumeUploadSuccess(true);
      toast.success("Resume updated successfully!");
      setResumeFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      // Refresh the displayed resume name
      try {
        const updated = await axiosInstance.get("/auth/profile");
        setCurrentResume(updated.data.primaryResume || null);
      } catch { /* non-fatal */ }
    } catch (err: any) {
      const msg = err.response?.data?.error || "Failed to upload resume";
      setResumeUploadError(msg);
      toast.error(msg);
    } finally {
      setResumeUploading(false);
    }
  };

  // Profile Picture Handlers
  const handleProfilePicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Photo must be under 5 MB. Please choose a smaller image.");
        e.target.value = "";
        return;
      }
      setProfilePicFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setProfilePicPreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfilePicUpload = async () => {
    if (!profilePicFile) {
      toast.error("Please select a profile picture first");
      return;
    }

    setUploadingProfilePic(true);

    const formDataUpload = new FormData();
    formDataUpload.append("profilePic", profilePicFile);

    try {
      const response = await axiosInstance.post("/auth/profile-pic", formDataUpload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setProfilePic(buildProfilePicUrl(response.data.profilePicUrl) || profilePicPreview);
      setProfilePicPreview(null);
      setProfilePicFile(null);
      if (profilePicInputRef.current) {
        profilePicInputRef.current.value = "";
      }
      toast.success("Profile picture updated!");
    } catch (err: any) {
      const msg = err.response?.data?.error || "Failed to upload profile picture";
      toast.error(msg);
    } finally {
      setUploadingProfilePic(false);
    }
  };

  const cancelProfilePicUpload = () => {
    setProfilePicPreview(null);
    setProfilePicFile(null);
    if (profilePicInputRef.current) {
      profilePicInputRef.current.value = "";
    }
  };

  if (loading) return <p>Loading profile...</p>;

  return (
    <div className="hb-profile-container">
      <button
        onClick={() => navigate("/dashboard")}
        className="hb-btn hb-btn-secondary"
      >
        ← Back to Dashboard
      </button>

      {/* Coach Message */}
      <div className="hb-coach-message">
        <span className="hb-coach-icon">👤</span>
        <p>Keep your profile updated for better job matches and a great impression!</p>
      </div>

      <div className="hb-profile-card">
        <h1 className="hb-heading-page">Your Profile</h1>

        {/* Profile Picture Section */}
        <div className="hb-profile-pic-section">
          <div className="hb-profile-pic-container">
            <div className="hb-profile-pic-avatar">
              {profilePicPreview ? (
                <img src={profilePicPreview} alt="Preview" className="hb-profile-pic-image" />
              ) : profilePic ? (
                <img src={profilePic} alt="Profile" className="hb-profile-pic-image" />
              ) : (
                <div className="hb-profile-pic-placeholder">
                  📸
                </div>
              )}
            </div>
            <div className="hb-profile-pic-upload">
              <input
                ref={profilePicInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfilePicChange}
                className="hb-profile-pic-input"
              />
              <button
                type="button"
                onClick={() => profilePicInputRef.current?.click()}
                className="hb-btn hb-btn-secondary hb-btn-sm"
              >
                Choose Photo
              </button>
              <p style={{ fontSize: "0.78rem", color: "#94a3b8", margin: "4px 0 0" }}>
                Max 5 MB · JPG, PNG, GIF
              </p>
              {profilePicPreview && (
                <div className="hb-profile-pic-actions">
                  <button
                    type="button"
                    onClick={handleProfilePicUpload}
                    disabled={uploadingProfilePic}
                    className="hb-btn hb-btn-primary hb-btn-sm"
                  >
                    {uploadingProfilePic ? '⏳ Uploading...' : '✅ Upload'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelProfilePicUpload}
                    className="hb-btn hb-btn-ghost hb-btn-sm"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
          <p className="hb-profile-pic-helper">Tip: Use a professional headshot. Max 2 MB — JPG, PNG, or GIF recommended.</p>
        </div>

        <form onSubmit={handleSubmit} className="hb-profile-form">
          {/* Personal Information Section */}
          <div className="hb-profile-section">
            <h2 className="hb-heading-section">👥 Personal Information</h2>
            
            <div className="hb-form-group">
              <label htmlFor="fullName" className="hb-label-form">Full Name</label>
              <input
                id="fullName"
                type="text"
                name="fullName"
                className="hb-input-form"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Enter your full name"
                readOnly={fullNameLocked}
                disabled={fullNameLocked}
              />
              {fullNameLocked ? (
                <p className="hb-helper-text" style={{ color: '#4a5568' }}>
                  Your full name is locked because it was set during signup.
                </p>
              ) : (
                <p className="hb-helper-text">This name will be locked after it is saved.</p>
              )}
            </div>

            <div className="hb-form-group">
              <label htmlFor="phone" className="hb-label-form">Phone Number</label>
              <input
                id="phone"
                type="tel"
                name="phone"
                className="hb-input-form"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(123) 456-7890"
              />
              <p className="hb-helper-text">We'll only use this if absolutely necessary</p>
            </div>
          </div>

          {/* Job Preferences Section */}
          <div className="hb-profile-section">
            <h2 className="hb-heading-section">🎯 Job Preferences</h2>
            
            <div className="hb-form-group">
              <label htmlFor="jobBoard" className="hb-label-form">Preferred Job Board</label>
              <select
                id="jobBoard"
                name="jobBoard"
                className="hb-input-form"
                value={formData.jobBoard}
                onChange={handleChange}
              >
                <option value="adzuna">Adzuna – UK, CA, AU, US</option>
                <option value="jsearch">JSearch – Indeed, LinkedIn, Glassdoor</option>
                <option value="remoteok">RemoteOK – Remote Jobs</option>
                <option value="themuse">The Muse – Company Culture</option>
                <option value="arbeitnow">Arbeitnow – EU Jobs</option>
              </select>
              <p className="hb-helper-text">Sets your default board in Advanced Search. All boards are available via Configure Boards.</p>
            </div>

            {/* Job Search Location */}
            <div className="hb-form-group">
              <label className="hb-label-form">Job Search Location</label>
              <div className="hb-form-row">
                <input
                  type="text"
                  className="hb-input-form"
                  placeholder="Country"
                  value={formData.jobSearchLocation.country}
                  onChange={(e) => handleLocationChange("country", e.target.value)}
                />
                <input
                  type="text"
                  className="hb-input-form"
                  placeholder="Province/State"
                  value={formData.jobSearchLocation.province}
                  onChange={(e) => handleLocationChange("province", e.target.value)}
                />
              </div>
              <p className="hb-helper-text">Where are you looking for jobs?</p>
            </div>

            {/* Preferred Sector */}
            <div className="hb-form-group">
              <label className="hb-label-form">Preferred Sector</label>
              <div className="hb-radio-grid" style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                {PREFERRED_SECTORS.map((sector) => (
                  <label key={sector} className="hb-checkbox-item">
                    <input
                      type="radio"
                      name="preferredSector"
                      value={sector}
                      checked={formData.preferredSector === sector}
                      onChange={() => setFormData(prev => ({ ...prev, preferredSector: sector }))}
                    />
                    <span>{sector}</span>
                  </label>
                ))}
              </div>
              <p className="hb-helper-text">Which sector are you targeting?</p>
            </div>

            {/* Preferred Industries */}
            <div className="hb-form-group">
              <label className="hb-label-form">Preferred Industries</label>
              <div className="hb-checkbox-grid">
                {COMMON_INDUSTRIES.map((industry) => (
                  <label key={industry} className="hb-checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.industries.includes(industry)}
                      onChange={() => handleIndustryToggle(industry)}
                    />
                    <span>{industry}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Preferred Job Titles */}
            <div className="hb-form-group">
              <label className="hb-label-form">Preferred Job Titles</label>
              <div className="hb-tag-input">
                <div className="hb-tags">
                  {formData.preferredJobTitles.map((title) => (
                    <span key={title} className="hb-tag">
                      {title}
                      <button type="button" onClick={() => handleRemoveJobTitle(title)}>×</button>
                    </span>
                  ))}
                </div>
                <div className="hb-tag-add">
                  <input
                    type="text"
                    placeholder="Add job title (e.g., Software Engineer)"
                    value={newJobTitle}
                    onChange={(e) => setNewJobTitle(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddJobTitle())}
                  />
                  <button type="button" onClick={handleAddJobTitle} className="hb-btn hb-btn-sm hb-btn-secondary">
                    Add
                  </button>
                </div>
              </div>
              <p className="hb-helper-text">What roles are you interested in? (max 5)</p>
            </div>
          </div>

          {/* Education & Experience Section */}
          <div className="hb-profile-section">
            <h2 className="hb-heading-section">🎓 Education & Experience</h2>
            
            {/* Qualifications */}
            <div className="hb-form-group">
              <label className="hb-label-form">Qualifications</label>
              <div className="hb-checkbox-grid">
                {QUALIFICATION_OPTIONS.map((qual) => (
                  <label key={qual} className="hb-checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.qualifications.includes(qual)}
                      onChange={() => handleQualificationToggle(qual)}
                    />
                    <span>{qual}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Field of Study — academic qualifications only (Professional Cert
                is captured as certifications below, not a field of study). */}
            {formData.qualifications.some((q) => q !== "Professional Cert") && (
              <div className="hb-form-group">
                <label className="hb-label-form">Field of Study</label>
                {formData.qualifications
                  .filter((qual) => qual !== "Professional Cert")
                  .map((qual) => (
                    <div key={qual} className="hb-form-subgroup">
                      <span className="hb-form-subgroup-label">{qual}:</span>
                      <input
                        type="text"
                        className="hb-input-form"
                        placeholder={`e.g., Computer Science`}
                        value={formData.programmeOfStudy[qual] || ""}
                        onChange={(e) => handleProgrammeChange(qual, e.target.value)}
                      />
                    </div>
                  ))}
              </div>
            )}

            {/* Experience Level */}
            <div className="hb-form-group">
              <label htmlFor="experienceLevel" className="hb-label-form">Experience Level</label>
              <select
                id="experienceLevel"
                name="experienceLevel"
                className="hb-input-form"
                value={formData.experienceLevel}
                onChange={handleChange}
              >
                <option value="">Select experience level</option>
                {EXPERIENCE_LEVELS.map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>

            {/* Years of Experience */}
            <div className="hb-form-group">
              <label htmlFor="yearsOfExperience" className="hb-label-form">Years of Experience</label>
              <input
                id="yearsOfExperience"
                type="number"
                name="yearsOfExperience"
                className="hb-input-form"
                min="0"
                max="50"
                value={formData.yearsOfExperience ?? ""}
                onChange={handleChange}
                placeholder="0"
              />
            </div>

            {/* Professional Certifications & Licenses — shown when the
                "Professional Cert" qualification is selected. Multi-entry; feeds
                job recommendations & matching. */}
            {formData.qualifications.includes("Professional Cert") && (
            <div className="hb-form-group">
              <label className="hb-label-form">Professional Certifications &amp; Licenses</label>
              <div className="hb-tag-input">
                <div className="hb-tags">
                  {formData.certifications.map((cert) => (
                    <span key={cert} className="hb-tag">
                      {cert}
                      <button type="button" onClick={() => handleRemoveCertification(cert)}>×</button>
                    </span>
                  ))}
                </div>
                <div className="hb-tag-add">
                  <input
                    type="text"
                    placeholder="Add certification (e.g., AWS, PMP, CFA)"
                    value={newCertification}
                    onChange={(e) => setNewCertification(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddCertification())}
                  />
                  <button type="button" onClick={handleAddCertification} className="hb-btn hb-btn-sm hb-btn-secondary">
                    Add
                  </button>
                </div>
              </div>
              <p className="hb-helper-text">Professional certifications, trade certificates, or licenses you hold — used to match you to relevant jobs</p>
            </div>
            )}

            {/* Action Button — sits at the bottom of the profile form */}
            <div className="hb-profile-actions">
              <button
                type="submit"
                disabled={saving}
                className={saving ? 'hb-btn hb-btn-primary hb-btn-loading' : 'hb-btn hb-btn-primary'}
              >
                {saving ? '⏳ Saving...' : '✅ Save Profile'}
              </button>
            </div>
          </div>
        </form>

        {/* Resume Upload Section */}
        <div className="hb-profile-section hb-resume-section" style={{ marginTop: '2.5rem' }}>
          <h2 className="hb-heading-section">📄 Your Resume</h2>

          {/* Current resume */}
          {currentResume ? (
            <div className="hb-current-resume">
              <span className="hb-current-resume-icon">📎</span>
              <div className="hb-current-resume-info">
                <span className="hb-current-resume-label">Current resume</span>
                <span className="hb-current-resume-name">{currentResume.fileName}</span>
              </div>
              <a
                href={currentResume.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hb-current-resume-download"
              >
                View / Download
              </a>
            </div>
          ) : (
            <p className="hb-no-resume-notice">No resume on file yet — upload one below.</p>
          )}

          <p className="hb-section-description" style={{ marginTop: '12px' }}>
            {currentResume ? 'Replace your resume:' : 'Upload a resume (PDF, DOCX, or TXT) for AI analysis and clarity review'}
          </p>

          <div className="hb-resume-upload-area">
            <div className="hb-resume-upload-icon">📄</div>
            <label htmlFor="resume" className="hb-resume-upload-label">
              {resumeFile ? resumeFile.name : 'Choose Resume File'}
            </label>
            <input
              id="resume"
              ref={fileInputRef}
              type="file"
              name="resume"
              onChange={handleResumeChange}
              accept=".pdf,.doc,.docx,.txt"
              className="hb-resume-upload-input"
            />
          </div>

          <button
            onClick={handleResumeUpload}
            disabled={resumeUploading || !resumeFile}
            className={resumeUploading || !resumeFile ? 'hb-btn hb-btn-primary hb-btn-loading' : 'hb-btn hb-btn-primary'}
          >
            {resumeUploading ? '⏳ Uploading...' : '📤 Upload Resume'}
          </button>

          {resumeUploadSuccess && (
            <div className="hb-alert hb-alert-success">
              <span>✅</span>
              <p>Resume updated successfully! Check Dashboard for clarity review.</p>
            </div>
          )}
          {resumeUploadError && (
            <div className="hb-alert hb-alert-error">
              <span>⚠️</span>
              <p>{resumeUploadError}</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Profile;